import NavBar from "../../components/common/NavBar";
import React, { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { GET_WAIVERS, UPDATE_WAIVER, DELETE_WAIVER } from "../../components/waiver/Waiver";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import CreateWaiverForm from "../../components/waiver/CreateWaiverForm";
import TextWaiverModal from "../../components/waiver/TextWaiverModal";
import WaiverFormModal from "../../components/waiver/WaiverFormModal";
import { toast } from "react-toastify";
import { confirmDelete } from "../../components/common/confirmDelete";
import { GlobalStyles } from "../../constants/styles";
import UpgradeNotice, { UpgradeNoticeLink } from "../../components/common/UpgradeNotice";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useActiveProfileOrgProOrElite from "../../components/common/hooks/useActiveProfileOrgProOrElite";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function WaiversPage() {
    const scopeId = "kbola-waivers-page";
    const colors = useThemeColors();
    const { t } = useTranslation();

    const { activeProfile } = useActiveProfile();

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 25;

    const [waivers, setWaivers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [textModalContent, setTextModalContent] = useState(null);
    const [editWaiver, setEditWaiver] = useState(null);

    useEffect(() => {
        if (!showModal) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [showModal]);

    const ui = useMemo(() => {
        return {
            wrapCard: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: "linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.18), rgba(0,0,0,0))",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.95)",
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            headerRight: { display: "flex", alignItems: "center", gap: 10 },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            body: { padding: 14 },

            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)",
                color: "#14151a",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            chipInfo: {
                border: "1px solid rgba(13,110,253,0.25)",
                background: "rgba(13,110,253,0.08)",
                color: "#0d6efd",
            },

            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },

            modalBackdrop: { backgroundColor: "rgba(0,0,0,0.55)" },
            modalDialog: { maxWidth: 920 },
            modalContent: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            modalHeader: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                zIndex: 3,
            },
            modalTitleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            modalBadge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            modalTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            modalSub: { margin: 0, fontSize: 12.5, fontWeight: 700, color: colors.textOnPrimary },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
                color: colors.textOnPrimary,
            },
            modalBody: { padding: 14, background: "#fff" },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const { hasAnyOrgProOrElite } = useActiveProfileOrgProOrElite();

    const { loading, error, refetch } = useQuery(GET_WAIVERS, {
        variables: { page, pageSize },
        skip: !activeProfile || !hasAnyOrgProOrElite,
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            setWaivers(data?.waivers?.objects || []);
            setTotalPages(data?.waivers?.pages || 1);
        },
    });

    const [updateWaiver] = useMutation(UPDATE_WAIVER, {
        onCompleted: () => {
            setEditWaiver(null);
            refetch();
        },
    });

    const [deleteWaiver] = useMutation(DELETE_WAIVER);

    const handleDelete = (waiver) => {
        confirmDelete({
            title: t("waiversPage.deleteConfirmTitle"),
            message: t("waiversPage.deleteConfirmMessage", { title: waiver.title }),
            confirmLabel: t("waiversPage.deleteConfirmButton"),
            cancelLabel: t("waiversPage.deleteCancelButton"),
            danger: true,
            onConfirm: async () => {
                try {
                    const { data } = await deleteWaiver({ variables: { id: waiver.id } });
                    if (data?.deleteWaiver?.success) {
                        setWaivers((prev) => prev.filter((w) => w.id !== waiver.id));
                        toast.success(t("waiversPage.deleteSuccess"), {
                            autoClose: 3000,
                            toastId: "waiver_deleted",
                            position: "top-center",
                            closeOnClick: true,
                        });
                    }
                } catch (err) {
                    toast.error(getLocalizedGraphQLError(err, t, "waiversPage.deleteFailed"), {
                        autoClose: 3000,
                        toastId: "waiver_delete_error",
                        position: "top-center",
                        closeOnClick: true,
                    });
                }
            },
        });
    };

    const navBarElem = {
        pageTitle: t("waiversPage.pageTitle"),
        links: [
            { pageTitle: t("waiversPage.settings"), navLink: null, active: false },
            { pageTitle: t("waiversPage.pageTitle"), navLink: null, active: true },
        ],
    };

    const showTextModal = (content) => setTextModalContent(content);

    const handleWaiverSave = async (input) => {
        try {
            const { data } = await updateWaiver({
                variables: { id: editWaiver.id, input },
            });

            const updatedWaiver = data?.updateWaiver?.waiver;
            if (updatedWaiver) {
                setWaivers((prev) => prev.map((w) => (w.id === updatedWaiver.id ? updatedWaiver : w)));
                setEditWaiver(null);
                toast.success(t("waiversPage.updateSuccess"));
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "waiversPage.updateFailed"));
        }
    };

    return (
        <>
            <div className="content-wrapper" id={scopeId}>
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    {error && (
                        <div className="alert alert-danger col-6 mx-auto text-center">
                            {getLocalizedGraphQLError(error, t, "waiversPage.loadFailed")}
                        </div>
                    )}

                    {!hasAnyOrgProOrElite ? (
                        <UpgradeNotice
                            planLabel={t("waiversPage.upgradePlanLabel")}
                            title={t("waiversPage.upgradeTitle")}
                            subtitle={t("waiversPage.upgradeSubtitle")}
                            ctaLabel={t("waiversPage.upgradeCta")}
                            to="/subscription/plans"
                        >
                            {t("waiversPage.upgradeTextPrefix")}{" "}
                            <UpgradeNoticeLink to="/subscription/plans">
                                {t("waiversPage.upgradeTextLink")}
                            </UpgradeNoticeLink>
                            .
                        </UpgradeNotice>
                    ) : (
                        <PaginatedFlatTable
                            title={t("waiversPage.tableTitle")}
                            page={page}
                            onAdd={() => setShowModal(true)}
                            pages={totalPages}
                            onPageChange={(p) => setPage(p)}
                            items={waivers}
                            loading={loading}
                            headers={[
                                t("waiversPage.tableHeaders.title"),
                                t("waiversPage.tableHeaders.type"),
                                t("waiversPage.tableHeaders.language"),
                                t("waiversPage.tableHeaders.createdBy"),
                                t("waiversPage.tableHeaders.actions"),
                            ]}
                            renderRow={(waiver, index) => (
                                <tr key={waiver.id || index}>
                                    <td style={{ fontWeight: 900, color: "#14151a" }}>
                                        {waiver.title || t("waiversPage.noValue")}
                                    </td>

                                    <td>
                                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 10 }}>
                                            <span
                                                style={{
                                                    ...ui.chip,
                                                    ...ui.chipInfo,
                                                }}
                                                title={t("waiversPage.waiverTypeLabel")}
                                            >
                                                <i className="fas fa-file-signature" />
                                                {waiver.waiverType === "DOCUMENT"
                                                    ? t("waiversPage.document")
                                                    : t("waiversPage.text")}
                                            </span>

                                            {waiver.waiverType === "DOCUMENT" && waiver.documentUrl ? (
                                                <a
                                                    href={waiver.documentUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm"
                                                    style={{ ...ui.btn, padding: "8px 10px" }}
                                                    title={t("waiversPage.view")}
                                                >
                                                    <i className="fas fa-eye" />
                                                    {t("waiversPage.view")}
                                                </a>
                                            ) : null}

                                            {waiver.waiverType === "TEXT" && waiver.content ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm"
                                                    style={{ ...ui.btn, padding: "8px 10px" }}
                                                    onClick={() => showTextModal(waiver.content)}
                                                    title={t("waiversPage.view")}
                                                >
                                                    <i className="fas fa-eye" />
                                                    {t("waiversPage.view")}
                                                </button>
                                            ) : null}
                                        </div>
                                    </td>

                                    <td style={{ color: "rgba(20,21,26,0.70)", fontWeight: 800 }}>
                                        {waiver.language || t("waiversPage.noValue")}
                                    </td>

                                    <td style={{ color: "rgba(20,21,26,0.70)", fontWeight: 800 }}>
                                        {waiver.createdBy?.email || t("waiversPage.noValue")}
                                    </td>

                                    <td className="text-right">
                                        <div style={{ display: "inline-flex", gap: 8 }}>
                                            <button
                                                type="button"
                                                style={ui.actionBtn}
                                                className="kbola-action kbola-action-edit"
                                                onClick={() => setEditWaiver(waiver)}
                                                title={t("waiversPage.editWaiver")}
                                            >
                                                <i className="fas fa-edit" />
                                            </button>

                                            <button
                                                type="button"
                                                style={{ ...ui.actionBtn, ...ui.actionBtnDanger }}
                                                className="kbola-action kbola-action-danger"
                                                onClick={() => handleDelete(waiver)}
                                                title={t("waiversPage.deleteWaiver")}
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        />
                    )}

                    {showModal && (
                        <div
                            id="kbola-waiver-create-modal"
                            className="modal fade show d-block"
                            tabIndex="-1"
                            role="dialog"
                            style={ui.modalBackdrop}
                        >
                            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document" style={ui.modalDialog}>
                                <div className="modal-content" style={ui.modalContent}>
                                    <div style={ui.modalHeader}>
                                        <div style={ui.modalTitleWrap}>
                                            <span style={ui.modalBadge}>{t("waiversPage.createBadge")}</span>
                                            <div style={{ minWidth: 0 }}>
                                                <h5 style={ui.modalTitle}>{t("waiversPage.createTitle")}</h5>
                                                <p style={ui.modalSub}>{t("waiversPage.createSubtitle")}</p>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            style={ui.closeBtn}
                                            onClick={() => setShowModal(false)}
                                            aria-label={t("textWaiverModal.close")}
                                            title={t("textWaiverModal.close")}
                                        >
                                            <i className="fas fa-times" />
                                        </button>
                                    </div>

                                    <div className="modal-body" style={ui.modalBody}>
                                        <CreateWaiverForm
                                            onCreated={(newWaiver) => {
                                                setWaivers((prev) => [newWaiver, ...prev]);
                                                setPage(1);
                                                toast.success(t("waiversPage.createSuccess"));
                                            }}
                                            onClose={() => setShowModal(false)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {textModalContent && (
                        <TextWaiverModal
                            content={textModalContent}
                            onClose={() => setTextModalContent(null)}
                        />
                    )}

                    {editWaiver && (
                        <WaiverFormModal
                            waiver={editWaiver}
                            onClose={() => setEditWaiver(null)}
                            onSave={handleWaiverSave}
                            colors={colors}
                        />
                    )}

                    <style>{`
            #${scopeId} .kbola-action{
              width: 38px;
              height: 38px;
              border-radius: 12px;
              border: 1px solid rgba(0,0,0,0.10);
              background: #fff;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              line-height: 1;
              transition: transform 120ms ease, box-shadow 120ms ease;
            }
            #${scopeId} .kbola-action:hover{
              transform: translateY(-1px);
              box-shadow: 0 10px 20px rgba(0,0,0,0.08);
            }
            #${scopeId} .kbola-action-edit{
              background: rgba(0,0,0,0.02);
              color: #14151a;
            }
            #${scopeId} .kbola-action-danger{
              border-color: rgba(220,53,69,0.35);
              background: rgba(220,53,69,0.06);
              color: #dc3545;
            }

            #${scopeId} button:focus,
            #${scopeId} .btn:focus,
            #${scopeId} a.btn:focus,
            #kbola-waiver-create-modal button:focus {
              outline: none;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            }

            #${scopeId} .kbola-table thead th{
              background: rgba(0,0,0,0.02);
              border-bottom: 1px solid rgba(0,0,0,0.06);
              color: rgba(20,21,26,0.72);
              font-size: 12.5px;
              font-weight: 900;
              text-transform: uppercase;
              letter-spacing: 0.02em;
              padding: 12px 14px;
            }
            #${scopeId} .kbola-table tbody td{
              border-top: 1px solid rgba(0,0,0,0.06);
              padding: 12px 14px;
              vertical-align: middle;
              color: rgba(20,21,26,0.86);
              font-weight: 700;
              font-size: 13.5px;
            }
            #${scopeId} .kbola-table tbody tr:hover{
              background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
            }

            #${scopeId} i.fas,
            #${scopeId} i.far,
            #${scopeId} i.fal,
            #${scopeId} i.fa,
            #${scopeId} i[class^="fa-"],
            #${scopeId} i[class*=" fa-"] {
              font-family: "Font Awesome 5 Free" !important;
              font-weight: 900;
            }
            #${scopeId} i.fab {
              font-family: "Font Awesome 5 Brands" !important;
              font-weight: 400;
            }
          `}</style>
                </section>
            </div>
        </>
    );
}
